import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Skeleton,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ThermostatOutlinedIcon from '@mui/icons-material/ThermostatOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

import {
  IPrevisaoDetalhada,
  IPrevisaoTempo,
  ISeveridadeAlerta,
  PrevisaoTempoService,
} from '../../shared/services/api/mercado';
import { CABECALHO_CLARO, CABECALHO_ESCURO, ICoresCabecalho } from './coresCabecalho';
import { IconeCondicao } from './IconeCondicao';

// Mesmo formato da "Previsão de chuva" do SIF (PrevisaoChuvaDialog):
// milímetros de chuva por dia, chance (potencial) de chuva, máxima/mínima e
// alertas — aqui com os alertas do dia a dia do comércio.

const AZUL_CHUVA = '#2563EB';

// Intensidade pelo volume do dia — escala de um único tom (azul claro → escuro).
const INTENSIDADES = [
  { ate: 0.2, rotulo: 'Sem chuva', fundo: 'rgba(104,122,112,.14)', texto: 'text.secondary' },
  { ate: 5, rotulo: 'Fraca', fundo: '#DBEAFE', texto: '#1E3A8A' },
  { ate: 15, rotulo: 'Moderada', fundo: '#93C5FD', texto: '#1E3A8A' },
  { ate: 30, rotulo: 'Forte', fundo: '#2563EB', texto: '#FFFFFF' },
  { ate: Infinity, rotulo: 'Muito forte', fundo: '#1E3A8A', texto: '#FFFFFF' },
];
const intensidade = (milimetros: number) => INTENSIDADES.find(i => milimetros < i.ate) ?? INTENSIDADES[INTENSIDADES.length - 1];

const potencial = (probabilidade: number) => {
  if (probabilidade >= 60) return 'Alto';
  if (probabilidade >= 30) return 'Médio';
  return 'Baixo';
};

const ESTILO_ALERTA: Record<ISeveridadeAlerta, { cor: string; rotulo: string; icone: JSX.Element }> = {
  perigo: { cor: '#B91C1C', rotulo: 'Perigo', icone: <ErrorOutlineRoundedIcon /> },
  atencao: { cor: '#B7791F', rotulo: 'Atenção', icone: <WarningAmberRoundedIcon /> },
  positivo: { cor: '#166534', rotulo: 'Favorável', icone: <CheckCircleOutlineRoundedIcon /> },
};

const fmtMm = (valor: number) => valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const rotuloDia = (dataIso: string, indice: number) => {
  const data = new Date(`${dataIso}T00:00:00`);
  const dataCurta = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  if (indice === 0) return { nome: 'Hoje', data: dataCurta };
  if (indice === 1) return { nome: 'Amanhã', data: dataCurta };
  return { nome: data.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', ''), data: dataCurta };
};

interface IDialogPrevisaoTempoProps {
  open: boolean;
  onClose: () => void;
  previsao: IPrevisaoTempo;
  // Cores do cabeçalho do painel inicial — o topo do dialog repete o mesmo
  // fundo; o corpo segue o tema (claro/escuro).
  cores: ICoresCabecalho;
}

// Previsão do tempo detalhada, aberta ao clicar no painel do cabeçalho. Os
// dados detalhados (14 dias, hora a hora) são buscados só quando o dialog abre.
export const DialogPrevisaoTempo: React.FC<IDialogPrevisaoTempoProps> = ({ open, onClose, previsao, cores }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const telaPequena = useMediaQuery(theme.breakpoints.down('sm'));
  const coresCondicao = isDark ? CABECALHO_ESCURO.condicao : CABECALHO_CLARO.condicao;
  const corEixo = theme.palette.text.secondary;
  const corGrade = isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.06)';

  const [detalhe, setDetalhe] = useState<IPrevisaoDetalhada | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);

  const carregar = () => {
    setCarregando(true);
    setErro(null);
    PrevisaoTempoService.obterPrevisaoDetalhada(previsao.latitude, previsao.longitude)
      .then((dados) => { setDetalhe(dados); setAtualizadoEm(new Date()); })
      .catch((e: Error) => setErro(e.message || 'Não foi possível carregar a previsão detalhada.'))
      .finally(() => setCarregando(false));
  };

  // Recarrega a cada abertura (e se a cidade mudar) — previsão fica velha rápido.
  useEffect(() => {
    if (open) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previsao.latitude, previsao.longitude]);

  const atual = detalhe?.atual;
  const dias = detalhe?.dias ?? [];
  const horas = detalhe?.horas ?? [];
  const hoje = dias[0];

  const soma = (lista: typeof dias) => lista.reduce((total, dia) => total + dia.chuvaMm, 0);
  const total7 = soma(dias.slice(0, 7));
  const totalPeriodo = soma(dias);
  const diasComChuva = dias.filter(dia => dia.chuvaMm >= 1).length;
  const diaMaisChuvoso = dias.reduce<typeof dias[number] | undefined>((maior, dia) => (!maior || dia.chuvaMm > maior.chuvaMm ? dia : maior), undefined);
  const indiceMaisChuvoso = diaMaisChuvoso ? dias.indexOf(diaMaisChuvoso) : -1;
  const chuva24h = horas.reduce((total, hora) => total + hora.chuvaMm, 0);

  const resumo = [
    { titulo: 'Próximas 24 h', valor: `${fmtMm(chuva24h)} mm`, detalhe: `máx. ${Math.max(0, ...horas.map(h => h.probabilidadeChuva))}% de chance` },
    { titulo: 'Próximos 7 dias', valor: `${fmtMm(total7)} mm`, detalhe: 'acumulado previsto' },
    { titulo: `Próximos ${dias.length} dias`, valor: `${fmtMm(totalPeriodo)} mm`, detalhe: `${diasComChuva} ${diasComChuva === 1 ? 'dia' : 'dias'} com chuva (≥ 1 mm)` },
    {
      titulo: 'Dia mais chuvoso',
      valor: diaMaisChuvoso && diaMaisChuvoso.chuvaMm >= 0.2 ? `${fmtMm(diaMaisChuvoso.chuvaMm)} mm` : '—',
      detalhe: diaMaisChuvoso && diaMaisChuvoso.chuvaMm >= 0.2
        ? `${rotuloDia(diaMaisChuvoso.data, indiceMaisChuvoso).nome} ${rotuloDia(diaMaisChuvoso.data, indiceMaisChuvoso).data}`
        : 'sem chuva no período',
    },
  ];

  const opcoesBase = {
    chart: { toolbar: { show: false }, fontFamily: 'inherit', background: 'transparent', animations: { enabled: false }, zoom: { enabled: false } },
    theme: { mode: isDark ? 'dark' : 'light' } as const,
    colors: [AZUL_CHUVA],
    plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: 'end' as const, columnWidth: '62%' } },
    grid: { borderColor: corGrade, strokeDashArray: 3, xaxis: { lines: { show: false } } },
    legend: { show: false },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: corEixo, fontSize: '11px' }, formatter: (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}` },
      title: { text: 'mm', style: { color: corEixo, fontSize: '11px', fontWeight: 500 } },
    },
  };

  const cartao = { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,.03)' : theme.palette.background.paper };

  const titulo = (texto: string, subtitulo?: string) => (
    <Box mb={subtitulo ? 0 : 1}>
      <Typography variant="subtitle2" fontWeight={700}>{texto}</Typography>
      {subtitulo && <Typography variant="caption" color="text.secondary">{subtitulo}</Typography>}
    </Box>
  );

  const carregandoInicio = carregando && !detalhe;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={telaPequena} scroll="paper">
      {/* Topo: agora */}
      <Box
        sx={{
          background: cores.fundo,
          color: cores.texto,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          position: 'relative',
          borderBottom: `1px solid ${cores.painelBorda}`,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Fechar"
          sx={{ position: 'absolute', top: 8, right: 8, color: cores.textoSecundario, '&:hover': { color: cores.texto } }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Box display="flex" alignItems="center" gap={0.5} pr={5}>
          <RoomOutlinedIcon sx={{ fontSize: 18, color: cores.destaque }} />
          <Typography fontWeight={700} noWrap>
            {previsao.cidade}{previsao.origemLocalizacao === 'ip' ? ' (aprox.)' : ''}
          </Typography>
          {atualizadoEm && (
            <Typography variant="caption" sx={{ color: cores.textoSecundario, ml: 0.5 }} noWrap>
              · atualizado às {atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={2} mt={1.5} flexWrap="wrap">
          <IconeCondicao icone={(atual ?? previsao.atual).condicao.icone} tamanho={telaPequena ? 52 : 64} cores={cores.condicao} />
          <Box>
            <Typography sx={{ fontSize: { xs: 40, sm: 52 }, fontWeight: 700, lineHeight: 1 }}>
              {Math.round((atual ?? previsao.atual).temperatura)}°C
            </Typography>
            <Typography variant="body2" sx={{ color: cores.textoSecundario, mt: 0.5 }}>
              {(atual ?? previsao.atual).condicao.texto}
              {atual ? ` · sensação de ${Math.round(atual.sensacao)}°C · umidade ${Math.round(atual.umidade)}%` : ''}
            </Typography>
          </Box>
          {hoje && (
            <Box ml={{ xs: 0, sm: 'auto' }} textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="body2" fontWeight={700}>
                Máx. {Math.round(hoje.tempMax)}° · Mín. {Math.round(hoje.tempMin)}°
              </Typography>
              <Typography variant="caption" sx={{ color: cores.chuva }}>
                {fmtMm(hoje.chuvaMm)} mm · {Math.round(hoje.probabilidadeChuva)}% de chance de chuva hoje
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {erro && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={carregar}>Tentar novamente</Button>}>
            {erro}
          </Alert>
        )}

        {/* Resumo da chuva */}
        <Box display="grid" gap={1.25} sx={{ gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
          {carregandoInicio
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={84} />)
            : resumo.map(item => (
              <Box key={item.titulo} p={1.5} sx={cartao}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>{item.titulo}</Typography>
                <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                  {item.valor}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{item.detalhe}</Typography>
              </Box>
            ))}
        </Box>

        {/* Alertas */}
        <Box>
          {titulo('Alertas')}
          {previsao.alertas.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nenhum alerta para o período.</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {previsao.alertas.map(alerta => {
                const estilo = ESTILO_ALERTA[alerta.severidade];
                return (
                  <Box
                    key={alerta.titulo}
                    display="flex"
                    gap={1.25}
                    p={1.25}
                    borderRadius={2}
                    sx={{ bgcolor: alpha(estilo.cor, isDark ? 0.16 : 0.07), borderLeft: `4px solid ${estilo.cor}` }}
                  >
                    <Box sx={{ color: estilo.cor, display: 'flex', flexShrink: 0, '& svg': { fontSize: 22 } }}>{estilo.icone}</Box>
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700}>
                        <Box component="span" sx={{ color: estilo.cor }}>{estilo.rotulo}:</Box> {alerta.titulo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{alerta.mensagem}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Chuva por dia */}
        <Box p={1.5} sx={cartao}>
          {titulo('Chuva prevista por dia (mm)', 'Passe o mouse (ou toque) na barra para ver a chance de chuva do dia.')}
          {carregandoInicio ? <Skeleton variant="rounded" height={240} sx={{ mt: 1 }} /> : dias.length > 0 && (
            <Chart
              type="bar"
              height={240}
              series={[{ name: 'Chuva', data: dias.map(dia => Number(dia.chuvaMm.toFixed(1))) }]}
              options={{
                ...opcoesBase,
                xaxis: {
                  categories: dias.map((dia, i) => { const r = rotuloDia(dia.data, i); return [r.nome, r.data]; }),
                  labels: { style: { colors: corEixo, fontSize: '10.5px' }, rotate: 0, hideOverlappingLabels: true },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                // Rótulo só nos dias com chuva de verdade, pra não poluir.
                dataLabels: {
                  enabled: true,
                  formatter: (v: number) => (v >= 1 ? v.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : ''),
                  offsetY: -18,
                  style: { fontSize: '10px', fontWeight: 700, colors: [theme.palette.text.primary] },
                },
                plotOptions: { bar: { ...opcoesBase.plotOptions.bar, dataLabels: { position: 'top' } } },
                tooltip: {
                  theme: isDark ? 'dark' : 'light',
                  y: {
                    formatter: (v: number, { dataPointIndex }: { dataPointIndex: number }) =>
                      `${fmtMm(v)} mm · ${Math.round(dias[dataPointIndex]?.probabilidadeChuva ?? 0)}% de chance`,
                  },
                },
              }}
            />
          )}
        </Box>

        {/* Próximas 24 horas */}
        {horas.length > 0 && (
          <Box p={1.5} sx={cartao}>
            {titulo('Próximas 24 horas (mm por hora)')}
            <Chart
              type="bar"
              height={170}
              series={[{ name: 'Chuva', data: horas.map(h => Number(h.chuvaMm.toFixed(1))) }]}
              options={{
                ...opcoesBase,
                plotOptions: { bar: { ...opcoesBase.plotOptions.bar, columnWidth: '70%' } },
                dataLabels: { enabled: false },
                xaxis: {
                  categories: horas.map(h => `${h.hora.slice(11, 13)}h`),
                  labels: { style: { colors: corEixo, fontSize: '10px' }, rotate: 0, hideOverlappingLabels: true },
                  tickAmount: 8,
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                tooltip: {
                  theme: isDark ? 'dark' : 'light',
                  y: {
                    formatter: (v: number, { dataPointIndex }: { dataPointIndex: number }) =>
                      `${fmtMm(v)} mm · ${Math.round(horas[dataPointIndex]?.probabilidadeChuva ?? 0)}% de chance · ${Math.round(horas[dataPointIndex]?.temperatura ?? 0)}°C`,
                  },
                },
              }}
            />
          </Box>
        )}

        {/* Dia a dia */}
        <Box>
          {titulo('Dia a dia')}
          <Box display="flex" flexDirection="column" gap={0.75}>
            {carregandoInicio && Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} variant="rounded" height={56} />)}
            {dias.map((dia, indice) => {
              const r = rotuloDia(dia.data, indice);
              const inten = intensidade(dia.chuvaMm);
              const chance = Math.round(dia.probabilidadeChuva);
              return (
                <Box
                  key={dia.data}
                  display="grid"
                  alignItems="center"
                  columnGap={1.5}
                  rowGap={0.75}
                  px={1.5}
                  py={1}
                  sx={{
                    ...cartao,
                    ...(indice === 0 && { borderColor: alpha(theme.palette.primary.main, 0.5), bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.05) }),
                    gridTemplateColumns: {
                      xs: '64px 1fr auto',
                      md: '84px minmax(130px, 1.2fr) 110px minmax(140px, 1.3fr) 96px 120px',
                    },
                    gridTemplateAreas: {
                      xs: '"dia cond mm" "pot pot pot" "temp temp vento"',
                      md: '"dia cond mm pot temp vento"',
                    },
                  }}
                >
                  <Box sx={{ gridArea: 'dia' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{r.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.data}</Typography>
                  </Box>

                  <Box sx={{ gridArea: 'cond' }} display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <IconeCondicao icone={dia.condicao.icone} tamanho={24} cores={coresCondicao} />
                    <Box minWidth={0}>
                      <Typography variant="body2" noWrap>{dia.condicao.texto}</Typography>
                      <Box
                        component="span"
                        sx={{ display: 'inline-block', px: 0.75, borderRadius: 1, fontSize: 10.5, fontWeight: 700, bgcolor: inten.fundo, color: inten.texto }}
                      >
                        {inten.rotulo}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ gridArea: 'mm', textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', color: dia.chuvaMm >= 0.2 ? AZUL_CHUVA : 'text.secondary' }}>
                      {fmtMm(dia.chuvaMm)}
                      <Box component="span" sx={{ fontSize: 12, fontWeight: 600, ml: 0.25 }}>mm</Box>
                    </Typography>
                    {dia.horasChuva > 0 && (
                      <Typography variant="caption" color="text.secondary">{dia.horasChuva} h de chuva</Typography>
                    )}
                  </Box>

                  <Box sx={{ gridArea: 'pot' }}>
                    <Box display="flex" justifyContent="space-between" mb={0.25}>
                      <Typography variant="caption" color="text.secondary">Chance de chuva: <b>{potencial(chance)}</b></Typography>
                      <Typography variant="caption" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{chance}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={chance}
                      aria-label={`Chance de chuva ${chance}%`}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: isDark ? 'rgba(255,255,255,.08)' : 'rgba(37,99,235,.10)',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: AZUL_CHUVA },
                      }}
                    />
                  </Box>

                  <Box sx={{ gridArea: 'temp' }} display="flex" alignItems="center" gap={0.5}>
                    <ThermostatOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight={700}>
                      <Box component="span" sx={{ color: '#DC2626' }}>{Math.round(dia.tempMax)}°</Box>{' '}
                      <Box component="span" sx={{ color: '#2563EB', fontWeight: 600 }}>{Math.round(dia.tempMin)}°</Box>
                    </Typography>
                  </Box>

                  <Box sx={{ gridArea: 'vento', justifySelf: { xs: 'end', md: 'start' } }} display="flex" alignItems="center" gap={0.5}>
                    <AirOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {Math.round(dia.ventoMax)} km/h
                      {dia.rajadaMax > dia.ventoMax ? ` · raj. ${Math.round(dia.rajadaMax)}` : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary">
          <WaterDropOutlinedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5, color: AZUL_CHUVA }} />
          Fonte: Open-Meteo. Chance de chuva = chance máxima de chuva no dia (Baixa &lt; 30%, Média 30–59%, Alta ≥ 60%).
          Intensidade pelo volume do dia: fraca &lt; 5 mm, moderada 5–15 mm, forte 15–30 mm, muito forte ≥ 30 mm.
          Temperatura: <Box component="span" sx={{ color: '#DC2626', fontWeight: 700 }}>máxima</Box> e <Box component="span" sx={{ color: '#2563EB', fontWeight: 700 }}>mínima</Box> do dia.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button
          startIcon={<OpenInNewRoundedIcon />}
          href={PrevisaoTempoService.linkPrevisaoCompleta(previsao)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Abrir no site de clima
        </Button>
        <Button variant="contained" disableElevation onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};
